import React, { createContext, useContext, useState, useEffect } from 'react'
import moment from 'moment'
import AuthStore from '../stores/AuthStore'
import LiveClassRequests from '../services/requests/LiveClassRequests'

const JitsiMiniPlayerContext = createContext()

export const useJitsiMiniPlayer = () => {
  const context = useContext(JitsiMiniPlayerContext)
  if (!context) {
    throw new Error('useJitsiMiniPlayer must be used within a JitsiMiniPlayerProvider')
  }
  return context
}

export const JitsiMiniPlayerProvider = ({ children }) => {
  const [isVisible, setIsVisible] = useState(false)
  const [position, setPosition] = useState(null)
  const [meetingData, setMeetingData] = useState(null)
  const [jitsiApi, setJitsiApi] = useState(null)
  const [returnPageInfo, setReturnPageInfo] = useState(null)
  const [shouldCheckForReturn, setShouldCheckForReturn] = useState(false)
  const [originalJitsiApi, setOriginalJitsiApi] = useState(null)
  const [originalContainerId, setOriginalContainerId] = useState(null)
  const [miniPlayerContainerId, setMiniPlayerContainerId] = useState(null)
  const [originalCloseRoomFunction, setOriginalCloseRoomFunction] = useState(null)
  const [isTutor, setIsTutor] = useState(false)
  const [isActivatingMiniPlayer, setIsActivatingMiniPlayer] = useState(false)
  const [classEndTime, setClassEndTime] = useState(null)

  const startMeeting = (data, originalApi = null, originalContainer = null, closeRoomFunction = null, tutorStatus = false, endTime = null) => {
    // Set flag to indicate we're activating mini player
    setIsActivatingMiniPlayer(true)
    sessionStorage.setItem('activatingMiniPlayer', 'true')

    setMeetingData(data)
    setReturnPageInfo(data.returnPageInfo)
    setIsVisible(true)
    setPosition(null)
    setClassEndTime(endTime)

    // Store original Jitsi API and container
    if (originalApi) {
      setOriginalJitsiApi(originalApi)
      setOriginalContainerId(originalContainer)
      setMiniPlayerContainerId('global-jitsi-container')

      setOriginalCloseRoomFunction(closeRoomFunction)

      setIsTutor(tutorStatus)

      // Wait for the mini player container to be created before moving the interface
      setTimeout(() => {
        moveJitsiToMiniPlayer(originalApi, originalContainer, 'global-jitsi-container')
        // Clear the activation flag after a short delay
        setTimeout(() => {
          setIsActivatingMiniPlayer(false)
          sessionStorage.removeItem('activatingMiniPlayer')
        }, 1000)
      }, 50)
    } else if (originalJitsiApi && originalContainerId) {
      // If we already have the original API stored (second activation), move the interface again
      setTimeout(() => {
        moveJitsiToMiniPlayer(originalJitsiApi, originalContainerId, 'global-jitsi-container')
        // Clear the activation flag after a short delay
        setTimeout(() => {
          setIsActivatingMiniPlayer(false)
          sessionStorage.removeItem('activatingMiniPlayer')
        }, 1000)
      }, 50)
    }

    setTimeout(() => {
      setShouldCheckForReturn(true)
    }, 2000)
  }

  const moveJitsiToMiniPlayer = (api, fromContainer, toContainer) => {
    if (api && fromContainer && toContainer) {
      try {
        const fromElement = document.getElementById(fromContainer)
        const toElement = document.getElementById(toContainer)

        if (fromElement && toElement) {
          // Clear the target container
          toElement.innerHTML = ''

          // Move all children from original container to mini player container
          while (fromElement.firstChild) {
            toElement.appendChild(fromElement.firstChild)
          }

          // Update the API's parent node reference
          if (api._parentNode) {
            api._parentNode = toElement
          }

          // Force Jitsi to resize and adapt to the new container
          setTimeout(() => {
            forceJitsiResize(api, toElement)
          }, 100)
        } else {
          console.warn('Containers not found:', { fromContainer, toContainer, fromElement: !!fromElement, toElement: !!toElement })
        }
      } catch (error) {
        console.error('Error moving Jitsi interface:', error)
      }
    }
  }

  const forceJitsiResize = (api, container) => {
    if (api && container) {
      try {
        // Trigger Jitsi's resize event
        if (api.getIFrame && api.getIFrame()) {
          const iframe = api.getIFrame()
          if (iframe && iframe.contentWindow) {
            // Dispatch resize event to Jitsi
            iframe.contentWindow.dispatchEvent(new Event('resize'))

            // Also trigger window resize for Jitsi's internal handlers
            iframe.contentWindow.dispatchEvent(new Event('orientationchange'))

            // Force iframe to resize
            iframe.style.width = '100%'
            iframe.style.height = '100%'
            iframe.style.maxWidth = '100%'
            iframe.style.maxHeight = '100%'
            iframe.style.overflow = 'hidden'
          }
        }

        // Force Jitsi to recalculate layout
        if (api.executeCommand) {
          // Try to force layout recalculation
          setTimeout(() => {
            try {
              // Trigger any available resize commands
              if (api.executeCommand) {
                api.executeCommand('resize')
              }
            } catch (e) {
              console.log('Resize command not available, using alternative method')
            }
          }, 200)
        }

        // Apply CSS to force proper sizing - more aggressive approach
        const jitsiElements = container.querySelectorAll('[data-testid="meeting"], [class*="jitsi"], iframe, div, *')
        jitsiElements.forEach(element => {
          element.style.width = '100%'
          element.style.height = '100%'
          element.style.maxWidth = '100%'
          element.style.maxHeight = '100%'
          element.style.overflow = 'hidden'
          element.style.boxSizing = 'border-box'
        })

        // Force container itself to have proper dimensions
        container.style.width = '100%'
        container.style.height = '100%'
        container.style.maxWidth = '100%'
        container.style.maxHeight = '100%'
        container.style.overflow = 'hidden'
        container.style.boxSizing = 'border-box'

        // Trigger a global resize event
        window.dispatchEvent(new Event('resize'))

        console.log('Jitsi resize forced successfully')
      } catch (error) {
        console.error('Error forcing Jitsi resize:', error)
      }
    }
  }

  const moveJitsiToOriginal = (api, fromContainer, toContainer) => {
    if (api && fromContainer && toContainer) {
      try {
        const fromElement = document.getElementById(fromContainer)
        const toElement = document.getElementById(toContainer)

        if (fromElement && toElement) {
          // Clear the target container
          toElement.innerHTML = ''

          // Move all children back to original container
          while (fromElement.firstChild) {
            toElement.appendChild(fromElement.firstChild)
          }

          // Update the API's parent node reference
          if (api._parentNode) {
            api._parentNode = toElement
          }

          // Force Jitsi to resize back to original container
          setTimeout(() => {
            forceJitsiResize(api, toElement)
          }, 100)

          console.log('Jitsi interface moved back to original container successfully')
        } else {
          console.warn('Containers not found for return:', { fromContainer, toContainer, fromElement: !!fromElement, toElement: !!toElement })
        }
      } catch (error) {
        console.error('Error moving Jitsi interface back:', error)
      }
    }
  }

  const stopMeeting = () => {
    // Move Jitsi interface back to original container before closing
    if (originalJitsiApi && originalContainerId && miniPlayerContainerId) {
      moveJitsiToOriginal(originalJitsiApi, miniPlayerContainerId, originalContainerId)
    }

    setIsVisible(false)
    setPosition(null)
    setMeetingData(null)
    setJitsiApi(null)
    setReturnPageInfo(null)
    setShouldCheckForReturn(false)
    // Don't clear original data to allow reactivation
    // setOriginalJitsiApi(null)
    // setOriginalContainerId(null)
    // setMiniPlayerContainerId(null)
    // setOriginalCloseRoomFunction(null)
    // setIsTutor(false)
  }

  const executeOriginalCloseRoom = () => {
    if (originalCloseRoomFunction && typeof originalCloseRoomFunction === 'function') {
      try {
        // Execute the original close room function
        const result = originalCloseRoomFunction()

        // If it's a promise, handle it
        if (result && typeof result.then === 'function') {
          result.then(() => {
            clearAllData()
          }).catch((error) => {
            console.error('Error in close room promise:', error)
            clearAllData()
          })
        } else {
          // If it's not a promise, clear data immediately
          clearAllData()
        }
      } catch (error) {
        // Try to execute the function in the original context
        try {
          // Navigate back to the original page and execute the function there
          if (returnPageInfo) {
            const fullPath = returnPageInfo.pathname + returnPageInfo.search + returnPageInfo.hash
            window.location.href = fullPath
          } else {
            clearAllData()
          }
        } catch (navigateError) {
          console.error('Error navigating to original page:', navigateError)
          clearAllData()
        }
      }
    } else {
      // Fallback to just stopping the meeting
      stopMeeting()
    }
  }

  const executeCloseRoomInOriginalContext = () => {
    // Navigate back to the original page to execute the close room function in the correct context
    if (returnPageInfo) {
      const fullPath = returnPageInfo.pathname + returnPageInfo.search + returnPageInfo.hash

      // Store a flag to indicate that we want to execute the close room function
      sessionStorage.setItem('executeCloseRoom', 'true')

      // Also store the current time to ensure accurate attendance registration
      sessionStorage.setItem('closeRoomTime', moment().utc().format())

      // Navigate back to the original page
      window.location.href = fullPath
    } else {
      clearAllData()
    }
  }

  const registerAttendanceDirectly = async (skipClear) => {
    // Extrai lesson_id e discipline da URL original e registra a saída
    if (returnPageInfo && returnPageInfo.pathname) {
      // Remove segmentos vazios para evitar deslocamentos de índice
      const pathParts = returnPageInfo.pathname.split('/').filter(Boolean)

      const liveclassIndex = pathParts.findIndex(part => part === 'liveclass')
      if (liveclassIndex === -1) {
        clearAllData()
        return
      }

      // Estrutura esperada: .../liveclass/:discipline_id/:class_id/:lesson_id/(classroom|...)
      const disciplineId = pathParts[liveclassIndex + 1]
      const lessonId = pathParts[liveclassIndex + 3]

      if (lessonId && disciplineId) {
        const params = {
          institution_id: AuthStore.institution_current.id,
          discipline: disciplineId,
          lesson_id: lessonId,
          user_id: AuthStore.user.id,
          leaving_time: moment().utc().format()
        }

        // Tentativas múltiplas para garantir o registro
        let attempts = 0
        const maxAttempts = 3

        while (attempts < maxAttempts) {
          try {
            attempts++
            await LiveClassRequests.updateStudentAttendance(params)
            break
          } catch (error) {
            console.error('Error registering attendance directly (attempt ' + attempts + '):', error)
            if (attempts === maxAttempts) {
              console.error('All attempts failed for direct registration')
            } else {
              await new Promise(resolve => setTimeout(resolve, 1000))
            }
          }
        }
      }
    }

    if (!skipClear) {
      clearAllData()
    }
  }

  const clearAllData = () => {
    // Clear all data when the meeting is actually ended
    setIsVisible(false)
    setPosition(null)
    setMeetingData(null)
    setJitsiApi(null)
    setReturnPageInfo(null)
    setShouldCheckForReturn(false)
    setOriginalJitsiApi(null)
    setOriginalContainerId(null)
    setMiniPlayerContainerId(null)
    setOriginalCloseRoomFunction(null)
    setIsTutor(false)
    setIsActivatingMiniPlayer(false)
    setClassEndTime(null)
    sessionStorage.removeItem('activatingMiniPlayer')
  }

  const isClassEnded = () => {
    if (!classEndTime) return false
    const now = moment()
    const endTime = moment(classEndTime)
    return now.isAfter(endTime)
  }

  // Add beforeunload listener to ensure attendance is registered
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (isVisible && !isTutor && returnPageInfo) {
        registerAttendanceDirectly()
      }
    }

    window.addEventListener('beforeunload', handleBeforeUnload)

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
  }, [isVisible, isTutor, returnPageInfo])

  const returnToOriginalPage = () => {
    if (returnPageInfo) {
      const fullPath = returnPageInfo.pathname + returnPageInfo.search + returnPageInfo.hash
      stopMeeting()
      window.location.href = fullPath
    }
  }

  const updatePosition = (newPosition) => {
    setPosition(newPosition)
  }

  const checkAndCloseIfOnOriginalPage = () => {
    if (isVisible && returnPageInfo && shouldCheckForReturn) {
      const currentPath = window.location.pathname + window.location.search + window.location.hash
      const originalPath = returnPageInfo.pathname + returnPageInfo.search + returnPageInfo.hash

      const normalizePath = (path) => path.replace(/\/$/, '') || '/'
      const normalizedCurrent = normalizePath(currentPath)
      const normalizedOriginal = normalizePath(originalPath)

      if (normalizedCurrent === normalizedOriginal) {
        console.log('Miniplayer: Returning to original page, closing miniplayer')
        stopMeeting()
      }
    }
  }

  const value = {
    isVisible,
    position,
    meetingData,
    jitsiApi,
    returnPageInfo,
    shouldCheckForReturn,
    originalJitsiApi,
    originalContainerId,
    miniPlayerContainerId,
    originalCloseRoomFunction,
    isTutor,
    isActivatingMiniPlayer,
    classEndTime,
    isClassEnded,
    startMeeting,
    stopMeeting,
    returnToOriginalPage,
    updatePosition,
    setJitsiApi,
    checkAndCloseIfOnOriginalPage,
    moveJitsiToMiniPlayer,
    moveJitsiToOriginal,
    forceJitsiResize,
    executeOriginalCloseRoom,
    clearAllData,
    executeCloseRoomInOriginalContext,
    registerAttendanceDirectly
  }

  return (
    <JitsiMiniPlayerContext.Provider value={value}>
      {children}
    </JitsiMiniPlayerContext.Provider>
  )
}
